import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  IconButton,
  Icon,
  useBreakpointValue,
} from '@chakra-ui/react';
import { LuArrowUp, LuChevronDown, LuChevronUp, LuPanelLeft, LuSparkles, LuSquarePen, LuX } from 'react-icons/lu';
import { HiSparkles } from 'react-icons/hi';
import { luna } from './lunaTheme';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import { useLuna } from '../../hooks/communication/useLuna';
import { useMyCompany } from '../../hooks/company/useCompany';
import LunaConversationSidebar from './LunaConversationSidebar';
import { OPEN_LUNA_CHAT_EVENT } from './lunaChatEvents';

// Luna's mark in the panel: a plain dark sparkle (pulses while she thinks).
const LunaMark = ({ size = '18px', pulse = false }) => (
  <Icon
    as={LuSparkles}
    boxSize={size}
    color="#1C1C1E"
    strokeWidth={1.8}
    flexShrink={0}
    sx={pulse ? {
      '@keyframes lunaPulse': { '0%, 100%': { opacity: 0.35 }, '50%': { opacity: 1 } },
      animation: 'lunaPulse 1.4s ease-in-out infinite',
    } : undefined}
  />
);

const LunaChatbot = ({ currentPath = '/dashboard' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company } = useMyCompany();
  const {
    askLuna,
    fetchMessages,
    saveMessage,
    createConversation,
    fetchConversations,
    fetchConversation,
    deleteConversation
  } = useLuna();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  // Desktop trigger: the top bar "Ask Luna" button.
  useEffect(() => {
    const open = () => {
      setIsOpen(true);
      setIsMinimized(false);
    };
    window.addEventListener(OPEN_LUNA_CHAT_EVENT, open);
    return () => window.removeEventListener(OPEN_LUNA_CHAT_EVENT, open);
  }, []);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Conversation state
  const [showSidebar, setShowSidebar] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);

  // Responsive: full-screen bottom sheet on mobile (base + sm, up to 480px)
  const isMobile = useBreakpointValue({ base: true, sm: true, md: false, lg: false, xl: false });

  // Check if we should show suggested questions (always show after the last bot message, even if there's a conversation history)
  // But don't show if the last message has a navigation suggestion waiting for confirmation
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  // Only show suggested questions after the welcome message (first bot message, no user messages yet)
  const hasUserMessages = messages.some(m => m.role === 'user');
  const shouldShowSuggestedQuestions = lastMessage?.role === 'bot' && !isLoading && historyLoaded && !lastMessage?.navigateTo && !hasUserMessages;

  // Load conversations list
  const loadConversations = useCallback(async () => {
    setIsLoadingConversations(true);
    try {
      const response = await fetchConversations(20, 0);
      if (response && response.success && response.data) {
        setConversations(response.data);
      }
    } catch (error) {
      console.error('[LunaChatbot] Error loading conversations:', error);
    } finally {
      setIsLoadingConversations(false);
    }
  }, [fetchConversations]);

  // Load messages for a specific conversation
  const loadConversationMessages = useCallback(async (conversationId) => {
    setIsLoadingHistory(true);
    try {
      const response = await fetchConversation(conversationId, 100);
      if (response && response.success && response.data) {
        const { messages: convMessages } = response.data;
        const formattedMessages = convMessages.map(msg => ({
          role: msg.role === 'user' ? 'user' : 'bot',
          content: msg.message,
          timestamp: msg.timestamp,
          navigateTo: msg.navigateTo || null
        }));

        // Add welcome message if empty
        if (formattedMessages.length === 0) {
          const welcomeMessage = {
            role: 'bot',
            content: t('dashboard.luna.welcome', 'Bonjour ! Je suis Luna, votre assistante virtuelle Hostea. Je peux vous aider à naviguer dans le dashboard et répondre à vos questions. Comment puis-je vous aider ?'),
            navigateTo: null,
          };
          setMessages([welcomeMessage]);
        } else {
          setMessages(formattedMessages);
        }
      }
    } catch (error) {
      console.error('[LunaChatbot] Error loading conversation messages:', error);
    } finally {
      setIsLoadingHistory(false);
      setHistoryLoaded(true);
    }
  }, [fetchConversation, t]);

  // Handle new conversation
  const handleNewConversation = useCallback(async () => {
    try {
      const response = await createConversation();
      if (response && response.success && response.data) {
        const newConv = response.data;
        setActiveConversationId(newConv._id);
        setConversations(prev => [newConv, ...prev]);
        // Clear messages and add welcome message
        const welcomeMessage = {
          role: 'bot',
          content: t('dashboard.luna.welcome', 'Bonjour ! Je suis Luna, votre assistante virtuelle Hostea. Je peux vous aider à naviguer dans le dashboard et répondre à vos questions. Comment puis-je vous aider ?'),
          navigateTo: null,
        };
        setMessages([welcomeMessage]);
        setHistoryLoaded(true);
      }
    } catch (error) {
      console.error('[LunaChatbot] Error creating conversation:', error);
    }
  }, [createConversation, t]);

  // Handle select conversation
  const handleSelectConversation = useCallback(async (conversationId) => {
    if (conversationId === activeConversationId) return;
    setActiveConversationId(conversationId);
    setHistoryLoaded(false);
    await loadConversationMessages(conversationId);
  }, [activeConversationId, loadConversationMessages]);

  // Handle delete conversation
  const handleDeleteConversation = useCallback(async (conversationId) => {
    try {
      await deleteConversation(conversationId);
      setConversations(prev => prev.filter(c => c._id !== conversationId));
      // If deleted conversation was active, select another or create new
      if (conversationId === activeConversationId) {
        const remaining = conversations.filter(c => c._id !== conversationId);
        if (remaining.length > 0) {
          handleSelectConversation(remaining[0]._id);
        } else {
          handleNewConversation();
        }
      }
    } catch (error) {
      console.error('[LunaChatbot] Error deleting conversation:', error);
    }
  }, [deleteConversation, activeConversationId, conversations, handleSelectConversation, handleNewConversation, t]);

  // Load conversations and messages when the chatbot opens
  useEffect(() => {
    if (isOpen && !historyLoaded && !isLoadingHistory) {
      const initializeChat = async () => {
        setIsLoadingHistory(true);
        try {
          console.log('[LunaChatbot] Loading conversations...');
          // First, load the conversations list
          const convResponse = await fetchConversations(20, 0);

          if (convResponse && convResponse.success && convResponse.data && convResponse.data.length > 0) {
            console.log('[LunaChatbot] Conversations loaded:', convResponse.data.length);
            setConversations(convResponse.data);

            // Select the most recent conversation
            const mostRecent = convResponse.data[0];
            setActiveConversationId(mostRecent._id);

            // Load messages for this conversation
            const msgResponse = await fetchConversation(mostRecent._id, 100);
            if (msgResponse && msgResponse.success && msgResponse.data) {
              const { messages: convMessages } = msgResponse.data;
              const formattedMessages = convMessages.map(msg => ({
                role: msg.role === 'user' ? 'user' : 'bot',
                content: msg.message,
                timestamp: msg.timestamp,
                navigateTo: msg.navigateTo || null
              }));

              if (formattedMessages.length === 0) {
                const welcomeMessage = {
                  role: 'bot',
                  content: t('dashboard.luna.welcome', 'Bonjour ! Je suis Luna, votre assistante virtuelle Hostea. Je peux vous aider à naviguer dans le dashboard et répondre à vos questions. Comment puis-je vous aider ?'),
                  navigateTo: null,
                };
                setMessages([welcomeMessage]);
              } else {
                setMessages(formattedMessages);
              }
            }
          } else {
            console.log('[LunaChatbot] No conversations found, creating new one...');
            // Create a new conversation
            const newConvResponse = await createConversation();
            if (newConvResponse && newConvResponse.success && newConvResponse.data) {
              const newConv = newConvResponse.data;
              setConversations([newConv]);
              setActiveConversationId(newConv._id);
            }
            // Add welcome message
            const welcomeMessage = {
              role: 'bot',
              content: t('dashboard.luna.welcome', 'Bonjour ! Je suis Luna, votre assistante virtuelle Hostea. Je peux vous aider à naviguer dans le dashboard et répondre à vos questions. Comment puis-je vous aider ?'),
              navigateTo: null,
            };
            setMessages([welcomeMessage]);
          }
          setHistoryLoaded(true);
        } catch (error) {
          console.error('[LunaChatbot] Error initializing chat:', error);
          // On error, add welcome message
          const welcomeMessage = {
            role: 'bot',
            content: t('dashboard.luna.welcome', 'Bonjour ! Je suis Luna, votre assistante virtuelle Hostea. Je peux vous aider à naviguer dans le dashboard et répondre à vos questions. Comment puis-je vous aider ?'),
            navigateTo: null,
          };
          setMessages([welcomeMessage]);
          setHistoryLoaded(true);
        } finally {
          setIsLoadingHistory(false);
        }
      };

      initializeChat();
    }
  }, [isOpen, fetchConversations, fetchConversation, createConversation, t, historyLoaded, isLoadingHistory]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      inputRef.current?.focus();
    }
  }, [messages, isOpen, isMinimized]);

  const handleSend = async (messageText = null) => {
    const messageToSend = messageText || inputValue.trim();
    if (!messageToSend || isLoading) return;

    console.log('[LunaChatbot] User message:', messageToSend);
    console.log('[LunaChatbot] Current path:', currentPath);
    
    // Check if this is a confirmation response (yes/no) to a navigation suggestion
    const messageLower = messageToSend.toLowerCase().trim();
    const isYes = /^(oui|yes|sí|y|o)$/i.test(messageLower);
    const isNo = /^(non|no|n)$/i.test(messageLower);
    
    // Find the last bot message with a navigation suggestion (search backwards)
    let lastBotMessageIndex = -1;
    let lastBotMessage = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'bot' && messages[i].navigateTo) {
        lastBotMessageIndex = i;
        lastBotMessage = messages[i];
        break;
      }
    }
    const hasPendingNavigation = lastBotMessageIndex !== -1 && lastBotMessage !== null;
    
    if ((isYes || isNo) && hasPendingNavigation) {
      // Save the confirmation message to history first
      try {
        await saveMessage(messageToSend, currentPath, 'user', activeConversationId);
        console.log('[LunaChatbot] Confirmation message saved to history:', messageToSend);
      } catch (error) {
        console.error('[LunaChatbot] Error saving confirmation message:', error);
        // Continue anyway
      }

      // Add to local messages
      setMessages((prev) => [...prev, { role: 'user', content: messageToSend }]);

      // Handle confirmation/cancellation
      if (isYes) {
        console.log('[LunaChatbot] User confirmed navigation:', lastBotMessage.navigateTo);
        // Add Luna's confirmation response
        const confirmationResponse = t('dashboard.luna.navigationConfirmed', 'Parfait ! Je vous emmène sur cette page.');
        try {
          await saveMessage(confirmationResponse, currentPath, 'assistant', activeConversationId);
          console.log('[LunaChatbot] Confirmation response saved to history');
        } catch (error) {
          console.error('[LunaChatbot] Error saving confirmation response:', error);
        }
        setMessages((prev) => [...prev, { 
          role: 'bot', 
          content: confirmationResponse,
          navigateTo: null 
        }]);
        
        // Remove navigateTo from the original message to hide the buttons
        setMessages((prev) => {
          const updated = [...prev];
          if (updated[lastBotMessageIndex]) {
            updated[lastBotMessageIndex] = { ...updated[lastBotMessageIndex], navigateTo: null };
          }
          return updated;
        });
        
        // Navigate to the page
        if (lastBotMessage.navigateTo && lastBotMessage.navigateTo !== window.location.pathname) {
          setTimeout(() => {
            navigate(lastBotMessage.navigateTo);
          }, 500);
        }
        setInputValue('');
        return;
      } else {
        console.log('[LunaChatbot] User cancelled navigation');
        // Add Luna's cancellation response
        const cancellationResponse = t('dashboard.luna.navigationCancelled', 'D\'accord, je reste ici. Comment puis-je vous aider d\'autre ?');
        try {
          await saveMessage(cancellationResponse, currentPath, 'assistant', activeConversationId);
          console.log('[LunaChatbot] Cancellation response saved to history');
        } catch (error) {
          console.error('[LunaChatbot] Error saving cancellation response:', error);
        }
        setMessages((prev) => [...prev, { 
          role: 'bot', 
          content: cancellationResponse,
          navigateTo: null 
        }]);
        
        // Remove navigateTo from the original message to hide the buttons
        setMessages((prev) => {
          const updated = [...prev];
          if (updated[lastBotMessageIndex]) {
            updated[lastBotMessageIndex] = { ...updated[lastBotMessageIndex], navigateTo: null };
          }
          return updated;
        });
        setInputValue('');
        return;
      }
    }
    
    setInputValue('');
    setMessages((prev) => [...prev, { role: 'user', content: messageToSend }]);
    setIsLoading(true);

    try {
      console.log('[LunaChatbot] Calling askLuna hook with conversationId:', activeConversationId);
      const response = await askLuna(messageToSend, currentPath, activeConversationId);

      console.log('[LunaChatbot] Hook response:', {
        success: response?.success,
        hasData: !!response?.data,
        reply: response?.data?.reply?.substring(0, 100),
        navigateTo: response?.data?.navigateTo,
      });

      // Update conversation in sidebar after message sent
      if (activeConversationId) {
        setConversations(prev => prev.map(conv =>
          conv._id === activeConversationId
            ? { ...conv, preview: messageToSend.substring(0, 200), lastMessageAt: new Date().toISOString() }
            : conv
        ));
      }
      
      if (response && response.success && response.data) {
        const botResponse = response.data.reply;
        const navigateTo = response.data.navigateTo;

        console.log('[LunaChatbot] Processing bot response:', {
          replyLength: botResponse?.length,
          navigateTo,
          currentPath: window.location.pathname,
        });

        // Add bot response with navigation suggestion (if present)
        // Navigation will only happen if user clicks "Yes" button
        // Allow navigateTo even if user is on calendar page (for specific reservation navigation)
        let finalNavigateTo = null;
        if (navigateTo) {
          // Always allow navigation if it's to a specific reservation (contains /calendar/)
          // Otherwise, only allow if it's different from current path
          if (navigateTo.includes('/calendar/') || navigateTo !== window.location.pathname) {
            finalNavigateTo = navigateTo;
          }
        }
        
        setMessages((prev) => [...prev, { 
          role: 'bot', 
          content: botResponse,
          navigateTo: finalNavigateTo
        }]);

        console.log('[LunaChatbot] Bot response added, navigation suggestion:', {
          originalNavigateTo: navigateTo,
          finalNavigateTo: finalNavigateTo,
          currentPath: window.location.pathname,
          willShowButtons: !!finalNavigateTo
        });
      } else {
        console.warn('[LunaChatbot] Invalid response structure:', response);
        setMessages((prev) => [
          ...prev,
          { role: 'bot', content: t('dashboard.luna.error', 'Désolé, une erreur est survenue. Veuillez réessayer.'), navigateTo: null },
        ]);
      }
    } catch (error) {
      console.error('[LunaChatbot] Error:', error);
      console.error('[LunaChatbot] Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
      });
      setMessages((prev) => [
        ...prev,
        { role: 'bot', content: t('dashboard.luna.error', 'Désolé, une erreur est survenue. Veuillez réessayer.'), navigateTo: null },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedQuestion = (question) => {
    handleSend(question);
  };

  // The finance question names what THIS company does with its owners: invoice them
  // when they collect the rent themselves, send them statements otherwise.
  const suggestedQuestions = [
    t('dashboard.luna.suggestedQuestions.start', 'Comment démarrer avec Hostea ?'),
    t('dashboard.luna.suggestedQuestions.today', "Qu'est-ce qui se passe aujourd'hui ?"),
    t('dashboard.luna.suggestedQuestions.reservations', 'Combien de réservations ce mois ?'),
    t('dashboard.luna.suggestedQuestions.automations', 'Comment fonctionnent les automatisations ?'),
    ...(company?.products?.finance === false ? [] : [
      company?.financialSettings?.collectionMode === 'owner'
        ? t('dashboard.luna.suggestedQuestions.ownerInvoicing', 'Comment facturer mes propriétaires ?')
        : t('dashboard.luna.suggestedQuestions.ownerStatements', 'Comment fonctionnent les relevés propriétaires ?'),
    ]),
  ];

  const handleNavigationConfirm = async (navigateTo, messageIndex) => {
    if (navigateTo && navigateTo !== window.location.pathname) {
      console.log('[LunaChatbot] User confirmed navigation to:', navigateTo);

      // Save confirmation message if it wasn't already saved (when clicking button, not typing)
      const lastUserMessage = messages[messages.length - 1];
      const confirmationText = t('dashboard.luna.yes', 'Oui');
      if (!lastUserMessage || lastUserMessage.role !== 'user' || !['oui', 'yes', 'sí', 'y', 'o'].includes(lastUserMessage.content.toLowerCase().trim())) {
        try {
          await saveMessage(confirmationText, currentPath, 'user', activeConversationId);
          console.log('[LunaChatbot] Confirmation message saved to history (from button)');
        } catch (error) {
          console.error('[LunaChatbot] Error saving confirmation message:', error);
        }
        // Add to local messages
        setMessages((prev) => [...prev, { role: 'user', content: confirmationText }]);
      }

      // Add Luna's confirmation response
      const confirmationResponse = t('dashboard.luna.navigationConfirmed', 'Parfait ! Je vous emmène sur cette page.');
      try {
        await saveMessage(confirmationResponse, currentPath, 'assistant', activeConversationId);
        console.log('[LunaChatbot] Confirmation response saved to history');
      } catch (error) {
        console.error('[LunaChatbot] Error saving confirmation response:', error);
      }
      setMessages((prev) => [...prev, { 
        role: 'bot', 
        content: confirmationResponse,
        navigateTo: null 
      }]);
      
      // Remove navigateTo from the original message to hide the buttons
      setMessages((prev) => {
        const updated = [...prev];
        if (updated[messageIndex]) {
          updated[messageIndex] = { ...updated[messageIndex], navigateTo: null };
        }
        return updated;
      });
      
      // Navigate to the page
      setTimeout(() => {
        navigate(navigateTo);
      }, 500);
    }
  };

  const handleNavigationCancel = async (messageIndex) => {
    console.log('[LunaChatbot] User cancelled navigation');

    // Save cancellation message if it wasn't already saved (when clicking button, not typing)
    const lastUserMessage = messages[messages.length - 1];
    const cancellationText = t('dashboard.luna.no', 'Non');
    if (!lastUserMessage || lastUserMessage.role !== 'user' || !['non', 'no', 'n'].includes(lastUserMessage.content.toLowerCase().trim())) {
      try {
        await saveMessage(cancellationText, currentPath, 'user', activeConversationId);
        console.log('[LunaChatbot] Cancellation message saved to history (from button)');
      } catch (error) {
        console.error('[LunaChatbot] Error saving cancellation message:', error);
      }
      // Add to local messages
      setMessages((prev) => [...prev, { role: 'user', content: cancellationText }]);
    }

    // Add Luna's cancellation response
    const cancellationResponse = t('dashboard.luna.navigationCancelled', 'D\'accord, je reste ici. Comment puis-je vous aider d\'autre ?');
    try {
      await saveMessage(cancellationResponse, currentPath, 'assistant', activeConversationId);
      console.log('[LunaChatbot] Cancellation response saved to history');
    } catch (error) {
      console.error('[LunaChatbot] Error saving cancellation response:', error);
    }
    setMessages((prev) => [...prev, { 
      role: 'bot', 
      content: cancellationResponse,
      navigateTo: null 
    }]);
    
    // Remove navigateTo from message to hide the buttons
    setMessages((prev) => {
      const updated = [...prev];
      if (updated[messageIndex]) {
        updated[messageIndex] = { ...updated[messageIndex], navigateTo: null };
      }
      return updated;
    });
  };

  if (!isOpen) {
    return (
      // Mobile only: on desktop the top bar "Ask Luna" button opens the panel.
      <Box position="fixed" bottom={[6, 6, 24]} right={[5, 5, 6]} zIndex={1000} display={['block', 'block', 'none']}>
        {/* Luna AI floating button — solid color pill, expands to label on hover */}
        <Box
          as="button"
          role="group"
          onClick={() => setIsOpen(true)}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg={luna.night}
          borderRadius="full"
          h="56px"
          minW="56px"
          px="14px"
          overflow="hidden"
          cursor="pointer"
          border="none"
          outline="none"
          color="white"
          boxShadow="0 10px 30px rgba(67, 56, 202, 0.35), 0 4px 12px rgba(28, 27, 58, 0.25)"
          transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
          _hover={{
            transform: 'translateY(-3px) scale(1.02)',
            boxShadow: '0 14px 40px rgba(67, 56, 202, 0.45), 0 6px 16px rgba(28, 27, 58, 0.3)',
            bg: luna.nightHover,
            pr: '20px',
          }}
          _active={{
            transform: 'translateY(0px) scale(1)',
          }}
          sx={{
            // Soft lunar "breathing" halo to draw attention to the AI affordance
            '@keyframes lunaPulse': {
              '0%, 100%': { boxShadow: '0 10px 30px rgba(67, 56, 202, 0.35), 0 4px 12px rgba(28, 27, 58, 0.25)' },
              '50%': { boxShadow: '0 10px 34px rgba(94, 92, 230, 0.55), 0 0 18px rgba(199, 194, 245, 0.35)' },
            },
            animation: 'lunaPulse 3.5s ease-in-out infinite',
            '&:hover': { animation: 'none' },
          }}
        >
          <Icon as={HiSparkles} boxSize="22px" color="#EDEBFF" flexShrink={0} />
          <Text
            as="span"
            fontSize="14px"
            fontWeight="700"
            letterSpacing="-0.01em"
            whiteSpace="nowrap"
            maxW="0"
            ml="0"
            opacity={0}
            color="white"
            transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
            _groupHover={{
              maxW: '90px',
              ml: '10px',
              opacity: 1,
            }}
          >
            Luna AI
          </Text>
        </Box>
      </Box>
    );
  }

  // Conduit-style copilot panel: plain white sheet, quiet header, Luna answers as
  // plain text (no bubble), the user's lines in a soft grey bubble, and a roomy
  // composer card at the bottom.
  const headerIconButton = {
    size: 'sm',
    variant: 'ghost',
    minW: '30px',
    h: '30px',
    borderRadius: '8px',
    color: '#8E8E93',
    _hover: { bg: '#F2F2F4', color: '#1C1C1E' },
  };

  return (
    <Box
      position="fixed"
      // Desktop: docked under the top bar "Ask Luna" button, inside the page card.
      top={['70px', '70px', '76px']}
      bottom={[0, 0, 'auto']}
      left={[0, 0, 'auto']}
      right={[0, 0, 5]}
      zIndex={1000}
      w={['auto', 'auto', showSidebar ? '700px' : '460px', showSidebar ? '740px' : '480px']}
      h={isMinimized ? '52px' : ['auto', 'auto', 'min(680px, calc(100vh - 92px))']}
      borderRadius={['16px 16px 0 0', '16px 16px 0 0', '18px']}
      bg="white"
      boxShadow="0 24px 64px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.04)"
      border="1px solid"
      borderColor="rgba(0, 0, 0, 0.08)"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      transformOrigin="top right"
      sx={{
        '@keyframes lunaPanelIn': {
          from: { opacity: 0, transform: 'scale(0.97) translateY(-6px)' },
          to: { opacity: 1, transform: 'scale(1) translateY(0)' },
        },
        animation: 'lunaPanelIn 0.2s cubic-bezier(0.4, 0, 0.2, 1) both',
      }}
    >
      {/* Header */}
      <HStack
        h="52px"
        flexShrink={0}
        px={3}
        justify="space-between"
        cursor={isMinimized ? 'pointer' : 'default'}
        onClick={() => isMinimized && setIsMinimized(false)}
        borderBottom={isMinimized ? 'none' : '1px solid'}
        borderColor="#F0F0F2"
      >
        <HStack spacing={1}>
          {!isMinimized && (
            <IconButton
              {...headerIconButton}
              icon={<Icon as={LuPanelLeft} boxSize="17px" strokeWidth={1.8} />}
              onClick={(e) => {
                e.stopPropagation();
                setShowSidebar(!showSidebar);
              }}
              aria-label={showSidebar ? t('dashboard.luna.hideSidebar', 'Masquer historique') : t('dashboard.luna.showSidebar', 'Afficher historique')}
            />
          )}
          <HStack spacing={2} pl={isMinimized ? 2 : 1}>
            <LunaMark size="16px" />
            <Text fontSize="15px" fontWeight="600" color="#1C1C1E" letterSpacing="-0.01em">Luna</Text>
          </HStack>
        </HStack>
        <HStack spacing={0.5}>
          {!isMinimized && (
            <IconButton
              {...headerIconButton}
              icon={<Icon as={LuSquarePen} boxSize="16px" strokeWidth={1.8} />}
              onClick={(e) => {
                e.stopPropagation();
                handleNewConversation();
              }}
              aria-label={t('dashboard.luna.newConversation', 'Nouvelle conversation')}
            />
          )}
          {!isMobile && (
            <IconButton
              {...headerIconButton}
              icon={<Icon as={isMinimized ? LuChevronUp : LuChevronDown} boxSize="17px" strokeWidth={1.8} />}
              onClick={(e) => {
                e.stopPropagation();
                setIsMinimized(!isMinimized);
              }}
              aria-label={isMinimized ? t('dashboard.luna.expand', 'Développer') : t('dashboard.luna.minimize', 'Réduire')}
            />
          )}
          <IconButton
            {...headerIconButton}
            icon={<Icon as={LuX} boxSize="17px" strokeWidth={1.8} />}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
              setIsMinimized(false);
            }}
            aria-label={t('dashboard.luna.close', 'Fermer')}
          />
        </HStack>
      </HStack>

      {!isMinimized && (
        <HStack flex="1" spacing={0} align="stretch" overflow="hidden" minH={0}>
          {/* Sidebar — full-screen overlay on mobile, side panel on desktop */}
          {showSidebar && (
            <LunaConversationSidebar
              conversations={conversations}
              activeConversationId={activeConversationId}
              isLoading={isLoadingConversations}
              isMobile={isMobile}
              onNewConversation={() => {
                handleNewConversation();
                if (isMobile) setShowSidebar(false);
              }}
              onSelectConversation={(id) => {
                handleSelectConversation(id);
                if (isMobile) setShowSidebar(false);
              }}
              onDeleteConversation={handleDeleteConversation}
            />
          )}

          {/* Chat area — hidden while the sidebar overlay is open on mobile */}
          <Box
            flex="1"
            minW={0}
            display={showSidebar && isMobile ? 'none' : 'flex'}
            flexDirection="column"
            overflow="hidden"
          >
            <VStack
              flex="1"
              minH={0}
              px={5}
              pt={5}
              pb={2}
              spacing={5}
              overflowY="auto"
              align="stretch"
              sx={{
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(0, 0, 0, 0.12)', borderRadius: '3px' },
              }}
            >
              {messages.map((msg, idx) => (
                msg.role === 'user' ? (
                  <Box key={idx} alignSelf="flex-end" maxW="85%" bg="#F2F2F4" color="#1C1C1E" px={4} py="10px" borderRadius="18px">
                    <Text fontSize="14.5px" lineHeight="1.5" whiteSpace="pre-wrap">{msg.content}</Text>
                  </Box>
                ) : (
                  <HStack key={idx} align="flex-start" spacing={3}>
                    <Box pt="3px"><LunaMark /></Box>
                    <VStack align="stretch" spacing={3} flex="1" minW={0}>
                      <Box
                        fontSize="14.5px"
                        lineHeight="1.6"
                        color="#1C1C1E"
                        sx={{
                          '& p': { mb: 2 },
                          '& p:last-child': { mb: 0 },
                          '& ul, & ol': { pl: 5, mb: 2 },
                          '& li': { mb: 1 },
                          '& strong': { fontWeight: 600 },
                          '& code': { bg: '#F2F2F4', px: 1, py: 0.5, borderRadius: 'md', fontSize: '13px' },
                          '& h3': { fontWeight: 600, fontSize: '15px', mt: 3, mb: 1 },
                          '& a': { color: '#2569C9', textDecoration: 'underline' },
                        }}
                      >
                        <Markdown>{msg.content}</Markdown>
                      </Box>
                      {/* Navigation confirmation - only for bot messages with navigateTo */}
                      {msg.navigateTo && (
                        <HStack spacing={2}>
                          <Button
                            size="sm" h="32px" px={4} borderRadius="10px"
                            bg="#1C1C1E" color="white" fontSize="13px" fontWeight="500"
                            _hover={{ bg: '#3A3A3C' }}
                            onClick={() => handleNavigationConfirm(msg.navigateTo, idx)}
                          >
                            {t('dashboard.luna.navigationGo', 'Y aller')}
                          </Button>
                          <Button
                            size="sm" h="32px" px={4} borderRadius="10px" variant="ghost"
                            color="#6E6E73" fontSize="13px" fontWeight="500"
                            _hover={{ bg: '#F2F2F4' }}
                            onClick={() => handleNavigationCancel(idx)}
                          >
                            {t('dashboard.luna.no', 'Non')}
                          </Button>
                        </HStack>
                      )}
                    </VStack>
                  </HStack>
                )
              ))}

              {/* Suggested questions — outlined rows under Luna's welcome */}
              {shouldShowSuggestedQuestions && (
                <VStack spacing={2} align="stretch" pl={8}>
                  {suggestedQuestions.map((question) => (
                    <Box
                      key={question}
                      as="button"
                      type="button"
                      textAlign="left"
                      px={4}
                      py="10px"
                      borderRadius="12px"
                      border="1px solid"
                      borderColor="#E5E5EA"
                      fontSize="13.5px"
                      color="#1C1C1E"
                      transition="background 0.15s ease, border-color 0.15s ease"
                      _hover={{ bg: '#F7F7F8', borderColor: '#D1D1D6' }}
                      onClick={() => handleSuggestedQuestion(question)}
                    >
                      {question}
                    </Box>
                  ))}
                </VStack>
              )}

              {isLoading && (
                <HStack spacing={3} align="center">
                  <LunaMark pulse />
                  <Text fontSize="14px" color="#8E8E93">{t('dashboard.luna.thinking', 'Luna réfléchit…')}</Text>
                </HStack>
              )}
              <div ref={messagesEndRef} />
            </VStack>

            {/* Composer card */}
            <Box p={3} pt={2} flexShrink={0}>
              <Box
                border="1px solid"
                borderColor="#E5E5EA"
                borderRadius="16px"
                bg="white"
                boxShadow="0 1px 2px rgba(0, 0, 0, 0.04)"
                transition="border-color 0.15s ease, box-shadow 0.15s ease"
                _focusWithin={{ borderColor: '#C7C7CC', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.06)' }}
              >
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={t('dashboard.luna.placeholderAsk', 'Demandez quelque chose à Luna…')}
                  variant="unstyled"
                  px={4}
                  pt={3}
                  pb={2}
                  fontSize="14.5px"
                  color="#1C1C1E"
                  _placeholder={{ color: '#AEAEB2' }}
                  disabled={isLoading}
                />
                <HStack justify="space-between" px={3} pb={3}>
                  <Text fontSize="12px" color="#AEAEB2" pl={1}>
                    {t('dashboard.luna.enterToSend', 'Entrée pour envoyer')}
                  </Text>
                  <IconButton
                    icon={<Icon as={LuArrowUp} boxSize="16px" strokeWidth={2.2} />}
                    onClick={() => handleSend()}
                    isDisabled={!inputValue.trim() || isLoading}
                    size="sm"
                    minW="32px"
                    h="32px"
                    borderRadius="full"
                    bg="#1C1C1E"
                    color="white"
                    _hover={{ bg: '#3A3A3C' }}
                    _disabled={{ bg: '#E5E5EA', color: 'white', cursor: 'not-allowed' }}
                    aria-label={t('dashboard.luna.send', 'Envoyer')}
                  />
                </HStack>
              </Box>
            </Box>
          </Box>
        </HStack>
      )}
    </Box>
  );
};

export default LunaChatbot;
